import { HttpClient } from '@angular/common/http';
import { Injectable, ViewContainerRef } from '@angular/core';
import { environment } from '@app/environments/environment';
import { FontFamiliesDataService } from '@app/shared/font-families/font-families.data.service';
import { BrandLibraryDataService } from '@app/shared/media-library/brand-library.data.service';
import { isOrphanElement } from '@creative/nodes';
import { convertVersionPropertiesToDtos, stringifyDesignDocument } from '@creative/serialization';
import { creativeToChecksum, updateChecksumOfCreatives } from '@creative/utils';
import { deserializeImageAssets } from '@studio/data/deserialization/asset';
import {
    deserializeCreativeset,
    removeGraphQlTypename
} from '@studio/data/deserialization/creativeset';
import { CREATE_DESIGNS, DELETE_DESIGNS, DELETE_SIZES, UPDATE_DESIGNS } from '@studio/data/graphql';
import {
    BEGIN_CREATIVE_GENERATION,
    UPDATE_APPROVAL_STATUS,
    UPDATE_CREATIVES,
    UpdateCreativeStatusVariables
} from '@studio/data/graphql/creative.queries';
import { GET_FOLDER_ID, UPDATE_CREATIVESET_STATE_ID } from '@studio/data/graphql/creativeset.queries';
import { CreativeSetDtoV2 } from '@studio/domain/api/sapi';
import { IDesign, ImageLibraryAsset, ISerializedDesign } from '@studio/domain/creativeset';
import { IBrand } from '@studio/domain/creativeset/brand';
import { IBrandLibrary } from '@studio/domain/creativeset/brand-library';
import {
    ApprovalStatus,
    CreativeUpdate,
    ICreative,
    ICreativeDto,
    ICreativesetUpdateResponse
} from '@studio/domain/creativeset/creative/creative';
import { ICreativeset } from '@studio/domain/creativeset/creativeset';
import { IElement } from '@studio/domain/creativeset/element';
import { CreativeSize } from '@studio/domain/creativeset/size';
import { ISerializedVersion, IVersion } from '@studio/domain/creativeset/version';
import { IFontFamily } from '@studio/domain/font-families';
import { IShowcaseBrand } from '@studio/domain/showcase';
import { distinctArrayById } from '@studio/utils/array';
import { cloneDeep } from '@studio/utils/clone';
import { handleError } from '@studio/utils/errors/errors';
import { Logger } from '@studio/utils/logger';
import { uuidv4 } from '@studio/utils/utils';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { FontFamiliesService } from '../font-families/state/font-families.service';
import { mergeVersions } from '../versions/versions.utils';
import { ICreateDesignInputType, IDesignInput, IPreparedDesigns } from './creativeset-mutations';

const API_ROUTE = `${environment.origins.sapi}/creativeSets`;

@Injectable({ providedIn: 'root' })
export class CreativesetDataService {
    brand: Readonly<IBrand | IShowcaseBrand>; // will be set once and never changes, hence not really async

    get brandLibrary(): IBrandLibrary {
        return this.brandLibraryDataService.getBrandLibrary();
    }

    private fontFamilies: IFontFamily[] = [];

    private _creativeset$ = new BehaviorSubject<ICreativeset>(this.creativeset);
    creativeset$ = this._creativeset$.asObservable().pipe(
        tap(creativeset => {
            this._creativeset = creativeset;
        })
    );

    private _creativeset: ICreativeset;
    get creativeset(): ICreativeset {
        return this._creativeset;
    }
    brandLibraryLoaded: Promise<void>;
    __appComponentViewRef: ViewContainerRef;

    private logger = new Logger('CreativesetDataService');

    constructor(
        private brandLibraryDataService: BrandLibraryDataService,
        private fontFamiliesService: FontFamiliesService,
        private fontFamiliesDataService: FontFamiliesDataService,
        private apolloClient: Apollo,
        private httpClient: HttpClient
    ) {}

    setCreativeset(creativeset: ICreativeset): void {
        this._creativeset$.next(creativeset);
    }

    setBrand(brand: IBrand | IShowcaseBrand): void {
        this.brand = brand;
    }

    /**
     * This emits to the creativeset$ observable for cases where
     * the creativeset is mutated outside of the mutation stream.
     * This function will be redundant when all mutations happens
     * in the streams instead.
     */
    creativesetChanged(): void {
        this._creativeset$.next(this.creativeset);
    }

    syncFonts(fontFamilies?: IFontFamily[]): Observable<IFontFamily[]> {
        this.logger.debug('Syncing fonts');
        const fonts = fontFamilies ?? this.fontFamiliesDataService.creativesetFontFamilies;
        this.fontFamiliesService.loadFontFamilies();
        return this.fontFamiliesService.fontFamilies$.pipe(
            take(1),
            tap(() => this.fontFamiliesService.setFontFamiliesOfCreativeSet(fonts))
        );
    }

    /**
     * If an element is deleted from the brand library, make sure that we clean up the references in the design elements.
     * @param  {IElement[]} elements
     * @returns void
     */
    cleanElementParentReferences(elements: IElement[]): void {
        elements.forEach(element => {
            const deletedElement = !this.brandLibrary.elements.find(e => e.id === element.parentId);
            if (deletedElement) {
                element.parentId = undefined;
            }
        });
    }

    getAccountSlug(): string {
        return (this.brand as IBrand).accountSlug || '';
    }

    getBrandSlug(): string {
        return (this.brand as IBrand).slug || '';
    }

    setFontFamilies(fontFamilies: IFontFamily[]): void {
        this.fontFamilies = fontFamilies;
    }

    getFontFamilies(): IFontFamily[] {
        return this.fontFamilies;
    }

    getCreativesFromVersionsOrAll(versions: IVersion[]): ICreative[] {
        if (!versions.length) {
            return [...this.creativeset.creatives];
        }

        return [...this.creativeset.creatives].filter(creative =>
            versions.find(v => v.id === creative.version.id)
        );
    }

    getCreativeset(creativesetId: string): Observable<ICreativeset> {
        return this.httpClient.get<CreativeSetDtoV2>(`${API_ROUTE}/${creativesetId}`).pipe(
            map(creativesetDto => {
                try {
                    return deserializeCreativeset(creativesetDto);
                } catch (e) {
                    throw new Error(`Could not deserialize creativeset`);
                }
            }),
            tap(creativeset => this.setCreativeset(creativeset))
        );
    }

    getFolderId(creativesetId: string): Observable<string> {
        return this.apolloClient
            .query({
                query: GET_FOLDER_ID,
                variables: {
                    id: creativesetId
                }
            })
            .pipe(map(response => cloneDeep(response.data.creativeset.folderId)));
    }

    setApprovalStatus(
        approvalStatus: ApprovalStatus,
        creatives: ICreative[],
        creativesetId: string
    ): Observable<ICreative[]> {
        const creativesToUpdate = creatives.map(creative => ({
            id: creative.id,
            approvalStatus: approvalStatus
        }));

        return this.apolloClient
            .mutate({
                mutation: UPDATE_APPROVAL_STATUS,
                variables: {
                    creativesetId: creativesetId,
                    creatives: creativesToUpdate
                }
            })
            .pipe(
                map(({ data }) => {
                    const updatedCreatives = data!.setApprovalStatusOnCreatives;

                    creatives.forEach(creative => {
                        const updatedCreative = updatedCreatives.find(uc => uc.id === creative.id);

                        if (!updatedCreative) {
                            return;
                        }

                        creative = {
                            ...creative,
                            approvalStatus: updatedCreative.approvalStatus || ApprovalStatus.None
                        };
                    });

                    return creatives;
                }),
                tap(() => this.creativesetChanged())
            );
    }

    updateCreatives(creatives: ICreative[]): Observable<ICreative[]> {
        const creativeUpdateList = creatives.reduce<UpdateCreativeStatusVariables['creatives']>(
            (acc, creative) => {
                creative.checksum = creativeToChecksum(
                    creative,
                    creative.version,
                    this.creativeset.stateId
                );
                return [
                    ...acc,
                    {
                        id: creative.id,
                        targetUrl: creative.targetUrl,
                        checksum: creative.checksum,
                        approvalStatus: creative.approvalStatus
                    }
                ];
            },
            []
        );

        return this.apolloClient
            .mutate({
                mutation: UPDATE_CREATIVES,
                variables: {
                    creatives: creativeUpdateList
                }
            })
            .pipe(
                map(({ data }) => {
                    const updatedCreatives = data!.updateCreatives;

                    creatives.forEach(creative => {
                        const updatedCreative = updatedCreatives.find(uc => uc.id === creative.id);

                        if (!updatedCreative) {
                            return;
                        }

                        creative = {
                            ...creative,
                            ...updatedCreative
                        };
                    });

                    return creatives;
                })
            );
    }

    createDesignsWithExistingSize(creativeInputs: ICreateDesignInputType[]): Observable<ICreativeset> {
        const newCreativesArray: CreativeUpdate[] = [];

        for (const { size, versions: creativesInputVersions } of creativeInputs) {
            // creativesInputVersions is an array of versions, so it can return many creatives
            const sizeId = size.id;
            const creatives = this.creativeset.creatives.filter(
                creative =>
                    creative.size.id === sizeId &&
                    !!creativesInputVersions.find(v => v.id === creative.version.id)
            );

            const versions = this.creativeset.versions.filter(
                ({ id }) => !!creativesInputVersions.find(v => v.id === id)
            );

            for (const creative of creatives) {
                const dirtyCreative = cloneDeep(creative); // Used for calculating new checksum

                dirtyCreative.version = versions.find(v => v.id === dirtyCreative.version.id)!;
                if (dirtyCreative.size) {
                    const design = creativeInputs.find(
                        input => input.size.id === dirtyCreative.size.id
                    );
                    if (design) {
                        const { id, name, elements, document, hasHeavyVideo } = design.design;
                        dirtyCreative.design = {
                            id: id || '',
                            name: name || '',
                            elements,
                            document,
                            hasHeavyVideo
                        };
                    }
                }

                const version = versions.find(v => v.id === creative.version.id)!;
                const newChecksum = creativeToChecksum(
                    dirtyCreative,
                    version,
                    this.creativeset.stateId
                );
                if (dirtyCreative.checksum !== newChecksum) {
                    dirtyCreative.checksum = newChecksum;
                    newCreativesArray.push({
                        id: dirtyCreative.id,
                        checksum: newChecksum,
                        targetUrl: dirtyCreative.targetUrl
                    });
                }
            }
        }

        return this.apolloClient
            .mutate({
                mutation: CREATE_DESIGNS,
                variables: {
                    designs: creativeInputs.reduce<any[]>(
                        (acc, creativeInput) => [
                            ...acc,
                            {
                                sizeId: creativeInput.size.id,
                                design: {
                                    name: creativeInput.design.name,
                                    document: stringifyDesignDocument(
                                        creativeInput.design.document,
                                        true
                                    ),
                                    elements: [...creativeInput.design.elements].map(element => ({
                                        ...element,
                                        __typename: undefined,
                                        properties: element.properties.map(prop => ({
                                            ...prop,
                                            __typename: undefined
                                        }))
                                    }))
                                },
                                versions: creativeInput.versions.map(v => ({
                                    id: v.id,
                                    properties: convertVersionPropertiesToDtos(v.properties)
                                }))
                            }
                        ],
                        []
                    ),
                    creatives: newCreativesArray,
                    creativesetId: this.creativeset.id
                }
            })
            .pipe(switchMap(() => this.getCreativeset(this.creativeset.id)));
    }

    updateDesignsInCreativeset(
        allVersions: IVersion[],
        designInputs: IDesignInput[],
        dirtyVersions: IVersion[],
        defaultVersion: IVersion
    ): Observable<
        { versions: IVersion[]; designs: IDesign[]; images: ImageLibraryAsset[] } | undefined
    > {
        if (designInputs.length === 0) {
            return of(undefined);
        }

        const {
            creatives,
            designs: designUpdates,
            versions: versionUpdates,
            dirtyCreatives
        } = this.prepareDesignsForSave(allVersions, designInputs, dirtyVersions, defaultVersion);

        if (creatives.length === 0) {
            return of(undefined); // No creative changed, no save needed
        }

        return this.apolloClient
            .mutate({
                mutation: UPDATE_DESIGNS,
                variables: {
                    creativesetId: this.creativeset.id,
                    designs: designUpdates.map(design => ({
                        design,
                        versions: versionUpdates
                    })),
                    creatives
                }
            })
            .pipe(
                switchMap(() => this.getCreativeset(this.creativeset.id)),
                map(creativeset => {
                    const { designs, images, versions } = creativeset;

                    return {
                        designs,
                        images,
                        versions
                    };
                }),
                tap(({ designs, images, versions }) => {
                    this.updateRelationsFromPartialResponse({ designs, images, versions });
                    this.updateCreativeChecksumsAndWeights(dirtyCreatives);
                })
            );
    }

    deleteDesignsInCreativeset(ids: string[]): Observable<string[]> {
        return this.apolloClient
            .mutate({
                mutation: DELETE_DESIGNS,
                variables: {
                    designIds: ids,
                    creativesetId: this.creativeset.id
                }
            })
            .pipe(
                map(({ data }) => {
                    const designsToClear = this.creativeset.designs.filter(design =>
                        data!.deleteDesigns.ids.includes(design.id)
                    );

                    const propertiesIdsToRemove = this.deleteDesignsFromClient(designsToClear);
                    return propertiesIdsToRemove;
                }),
                tap(() => this.creativesetChanged())
            );
    }

    deleteSizesInCreativeset(sizes: CreativeSize[]): Observable<ICreativeset> {
        return this.apolloClient
            .mutate({
                mutation: DELETE_SIZES,
                variables: {
                    creativesetId: this.creativeset.id,
                    ids: sizes.map(s => s.id)
                }
            })
            .pipe(
                map(({ data, errors }) => {
                    if (!data || errors) {
                        handleError('Could not delete sizes in creativeset', {
                            contexts: { originalError: errors }
                        });
                    }
                }),
                switchMap(() => this.getCreativeset(this.creativeset.id))
            );
    }

    updateStateId(): Observable<void> {
        const stateId = uuidv4();
        return this.apolloClient
            .mutate({
                mutation: UPDATE_CREATIVESET_STATE_ID,
                variables: {
                    creativesetId: this.creativeset.id,
                    stateId: stateId,
                    creatives: this.creativeset.creatives.map(creative => ({
                        id: creative.id,
                        checksum: creativeToChecksum(creative, creative.version, stateId)
                    }))
                }
            })
            .pipe(
                switchMap(() => this.getCreativeset(this.creativeset.id)),
                map(() => void 0)
            );
    }

    publishChanges(creatives: ICreative[]): Observable<{
        creatives: Pick<ICreativeDto, 'id' | 'checksum'>[];
        failures: Pick<ICreativeDto, 'id' | 'checksum'>[];
    }> {
        const creativeIds = creatives.map(creative => creative.id);

        return this.apolloClient
            .mutate({
                mutation: BEGIN_CREATIVE_GENERATION,
                variables: {
                    creativeIds,
                    creativesetId: this.creativeset.id
                }
            })
            .pipe(map(({ data }) => data?.beginCreativeGeneration!));
    }

    prepareDesignsForSave(
        allVersions: IVersion[],
        designInputs: IDesignInput[],
        dirtyVersions: IVersion[],
        defaultVersion: IVersion
    ): IPreparedDesigns {
        const { creatives, stateId, elements } = this.creativeset;

        const partialCreativeset: Pick<ICreativeset, 'designs' | 'elements' | 'creatives'> = {
            creatives: cloneDeep(creatives),
            designs: designInputs.map(design => {
                return {
                    id: design.id!,
                    name: design.name,
                    document: design.document,
                    hasHeavyVideo: design.hasHeavyVideo,
                    elements: cloneDeep(design.elements)
                };
            }),
            elements: elements
        };

        for (const creative of partialCreativeset.creatives) {
            if (creative.design) {
                const design = designInputs.find(d => creative.design!.id === d.id)!;
                if (!design) {
                    continue;
                }

                design.elements.forEach(element => {
                    if (!creative.design!.elements.find(e => e.id === element.id)) {
                        creative.design!.elements.push(element);
                    }
                });

                creative.design.elements.forEach(element => {
                    const elementFromDesign = design.elements.find(e => e.id === element.id);
                    if (!elementFromDesign) {
                        creative.design!.elements = creative.design!.elements.filter(
                            el => el.id !== element.id
                        );
                    } else {
                        // Update the name with the current value. Used when no others modifications were done
                        element.name = elementFromDesign.name;
                        element.properties = elementFromDesign.properties;
                    }
                });

                creative.design.document = design.document;
            }
        }

        let dirtyCreatives = partialCreativeset.creatives
            .filter(creative => creative.design)
            .filter(
                creative =>
                    designInputs.find(design => creative.design!.id === design.id) ||
                    dirtyVersions.find(version => creative.version.id === version.id)
            );

        dirtyCreatives = updateChecksumOfCreatives(dirtyCreatives, allVersions, stateId);

        if (!dirtyVersions.find(v => v.id === defaultVersion.id)) {
            dirtyVersions.push(defaultVersion);
        }

        return {
            creatives: dirtyCreatives.map(creative => ({
                id: creative.id,
                targetUrl: creative.targetUrl,
                checksum: creative.checksum
            })),
            dirtyCreatives,
            designs: designInputs.map(d => {
                return {
                    id: d.id,
                    name: d.name,
                    document: stringifyDesignDocument(d.document, true),
                    elements: d.elements.map(element => {
                        const ret = {
                            ...element,
                            properties: element.properties.map(prop => {
                                removeGraphQlTypename(prop);
                                if (prop.versionPropertyId) {
                                    prop.value = undefined;
                                }
                                return prop;
                            })
                        };
                        removeGraphQlTypename(ret);
                        return ret;
                    })
                } as ISerializedDesign;
            }),
            versions: dirtyVersions.map(v => ({
                id: v.id,
                properties: convertVersionPropertiesToDtos(v.properties)
            })) as ISerializedVersion[]
        };
    }

    private updateRelationsFromPartialResponse(response: ICreativesetUpdateResponse): void {
        const creativeset = this.creativeset;

        // Get orphans in order to TP to work after saving
        const orphans = creativeset.elements.filter(element => isOrphanElement(element, creativeset));

        creativeset.designs = distinctArrayById([...response.designs, ...creativeset.designs]);

        creativeset.designs.forEach(design => {
            const relatedCreatives = creativeset.creatives.filter(
                creative => creative.design?.id === design.id
            );
            relatedCreatives.forEach(creative => (creative.design = design));
        });

        creativeset.versions = mergeVersions(creativeset.versions, response.versions);

        if (response.images) {
            const deserializedImages = deserializeImageAssets(response.images);
            creativeset.images = distinctArrayById([...deserializedImages, ...creativeset.images]);
        }

        const elements: IElement[] = [];
        elements.push(...orphans);
        for (const design of creativeset.designs) {
            // Reverse modifies the array, therefore the spreading
            [...design.elements].reverse().forEach(element => {
                removeGraphQlTypename(element);
                element.properties.forEach(property => removeGraphQlTypename(property));
                const elementExists = elements.some(el => el.id === element.id);
                if (!elementExists) {
                    // Unshift so the order of elements doesn't change. Needed in TP
                    elements.unshift(element);
                }
            });
        }

        creativeset.elements = elements;
    }

    private deleteDesignsFromClient(deletedDesigns: IDesign[]): string[] {
        const { creatives, versions, elements } = this.creativeset;
        const deletedDesignIds = deletedDesigns.map(d => d.id);
        const updatedElements: IElement[] = [];
        const versionPropertyIds: string[] = [];

        for (const creative of creatives) {
            if (creative.design && deletedDesignIds.includes(creative.design.id)) {
                creative.design = undefined;
            }
        }

        this.creativeset.designs = this.creativeset.designs.filter(
            d => !deletedDesignIds.includes(d.id)
        );

        for (const element of elements) {
            for (const design of this.creativeset.designs) {
                const designHasElement = design.elements.some(el => el.id === element.id);

                if (designHasElement) {
                    const elementExists = updatedElements.some(el => el.id === element.id);

                    if (!elementExists) {
                        updatedElements.push(element);
                    }

                    for (const property of element.properties) {
                        if (property.versionPropertyId) {
                            versionPropertyIds.push(property.versionPropertyId);
                        }
                    }
                }
            }
        }

        this.creativeset.elements = updatedElements;

        const propertiesIdsToRemove = new Set<string>();
        for (const version of versions) {
            for (const property of version.properties) {
                if (!versionPropertyIds.includes(property.id)) {
                    propertiesIdsToRemove.add(property.id);
                }
            }
        }

        return Array.from(propertiesIdsToRemove);
    }

    private updateCreativeChecksumsAndWeights(dirtyCreatives: ICreative[]): void {
        for (const dirtyCreative of dirtyCreatives) {
            const oldCreative = this.creativeset.creatives.find(
                creative => creative.id === dirtyCreative.id
            );

            if (oldCreative && oldCreative.checksum !== dirtyCreative.checksum) {
                oldCreative.checksum = dirtyCreative.checksum;
            }
        }

        this.creativesetChanged();
    }

    onVersionCreated(createdVersions: IVersion[], newCreatives: ICreative[]): void {
        this.creativeset.versions = mergeVersions(this.creativeset.versions, createdVersions);
        this.creativeset.creatives.push(...cloneDeep(newCreatives));
        this.creativesetChanged();
    }

    onVersionsUpdated(updatedVersions: IVersion[]): void {
        const nonUpdatedVersions = this.creativeset.versions.filter(
            version => !updatedVersions.find(updatedVersion => updatedVersion.id === version.id)
        );
        this.creativeset.versions = [...nonUpdatedVersions, ...updatedVersions];

        const updatedCreatives = this.creativeset.creatives.filter(creative =>
            updatedVersions.find(updatedVersion => creative.version.id === updatedVersion.id)
        );

        this.updateCreativeChecksumsAndWeights(updatedCreatives);
    }

    onVersionsDeleted(versionIds: string[]): void {
        this.creativeset.versions = this.creativeset.versions.filter(v => !versionIds.includes(v.id));
        this.creativeset.creatives = this.creativeset.creatives.filter(
            c => !versionIds.includes(c.version.id)
        );
        this.creativesetChanged();
    }

    onDefaultVersionUpdated(versionId: string): void {
        if (versionId === this.creativeset.defaultVersion.id) {
            return;
        }

        this.creativeset.defaultVersion = this.creativeset.versions.find(v => v.id === versionId)!;
        this.creativesetChanged();
    }
}
